import type { RootState } from "@/store/types";
import {
  selectIsSuperAdmin,
  selectRuntimeRolePermissionMap,
} from "@/store/slices/auth/auth.selectors";
import {
  hasRuntimePermission,
  hasExplicitRuntimePermission,
  type RolePermissionKey,
} from "./role-permissions";

type RuntimePermissionError = {
  error: { status: number | string; data: { message: string } };
};

type RuntimeContextFactory<TContext> = (
  state: RootState,
) => TContext | RuntimePermissionError;

export const DEFAULT_RUNTIME_PERMISSION_MESSAGE =
  "You do not have permission to perform this action. Contact your administrator.";

function isRuntimePermissionError<TContext>(
  result: TContext | RuntimePermissionError,
): result is RuntimePermissionError {
  return typeof result === "object" && result !== null && "error" in result;
}

export function validateRuntimePermission(
  state: RootState,
  permissionKey: RolePermissionKey,
  message = DEFAULT_RUNTIME_PERMISSION_MESSAGE,
  requireExplicitPermission = false,
): true | RuntimePermissionError {
  const permissionMap = selectRuntimeRolePermissionMap(state);
  const isSuperAdmin = selectIsSuperAdmin(state);
  if ((requireExplicitPermission ? hasExplicitRuntimePermission : hasRuntimePermission)(permissionMap, permissionKey, isSuperAdmin)) {
    return true;
  }
  return { error: { status: 403, data: { message } } };
}

export function authorizeRuntimePermission(
  state: RootState,
  permissionKey: RolePermissionKey,
  message = DEFAULT_RUNTIME_PERMISSION_MESSAGE,
): true | RuntimePermissionError {
  return validateRuntimePermission(state, permissionKey, message);
}

export function getAuthorizedRuntimeContext<TContext>(
  state: RootState,
  getContext: RuntimeContextFactory<TContext>,
  permissionKey: RolePermissionKey,
  message = DEFAULT_RUNTIME_PERMISSION_MESSAGE,
  requireExplicitPermission = false,
): TContext | RuntimePermissionError {
  const context = getContext(state);
  if (isRuntimePermissionError(context)) return context;

  const permissionResult = validateRuntimePermission(
    state,
    permissionKey,
    message,
    requireExplicitPermission,
  );
  if (isRuntimePermissionError(permissionResult)) return permissionResult;

  return context;
}

export function authorizeRuntimeContext<TContext>(
  state: RootState,
  context: TContext,
  permissionKey: RolePermissionKey,
  message = DEFAULT_RUNTIME_PERMISSION_MESSAGE,
): TContext | RuntimePermissionError {
  const permissionResult = validateRuntimePermission(
    state,
    permissionKey,
    message,
  );
  if (isRuntimePermissionError(permissionResult)) return permissionResult;

  return context;
}
